import { useQuery } from '@tanstack/react-query';
import { Sparkles } from 'lucide-react';
import { api } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDate } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth';
import { ROLE_RANK } from '@praxara/shared/constants';

// TODO(model-registry): write/edit UI deferred; this page is read-only.

interface ModelEntry {
  id: string;
  taskKey: string;
  primaryProvider: string | null;
  primaryModel: string | null;
  fallbacks: { provider: string; model: string }[] | null;
  isActive: boolean;
  updatedAt: string;
}

export function AdminModelRegistryPage() {
  const user = useAuthStore((s) => s.user);
  const isAdmin = user && (user.isSuperAdmin || ROLE_RANK[user.role] >= ROLE_RANK['ADMIN']);

  const entries = useQuery({
    queryKey: ['admin-model-registry'],
    queryFn: async () => {
      try {
        return (await api.get('/api/admin/model-registry?pageSize=200')).data;
      } catch {
        return { items: [] };
      }
    },
    enabled: !!isAdmin,
  });

  if (!isAdmin) {
    return (
      <EmptyState
        icon={Sparkles}
        title="Administrator access required"
        description="Only ADMIN or SUPER_ADMIN users can view the model registry."
      />
    );
  }

  const items: ModelEntry[] = entries.data?.items ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Model registry</h1>
        <p className="text-sm text-muted-foreground">
          Task-to-model routing with fallbacks. Editing is deferred; this view is read-only.
        </p>
      </div>

      {entries.isLoading ? (
        <Skeleton className="h-64" />
      ) : items.length === 0 ? (
        <EmptyState
          icon={Sparkles}
          title="No registry entries"
          description="Seed the ModelRegistry table to configure task routing."
        />
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Routing table</CardTitle>
            <CardDescription>
              Each task key resolves to a primary provider/model with ordered fallbacks.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Task key</TableHead>
                  <TableHead>Primary</TableHead>
                  <TableHead>Fallbacks</TableHead>
                  <TableHead>Active</TableHead>
                  <TableHead>Updated</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((m) => (
                  <TableRow key={m.id}>
                    <TableCell className="font-mono text-xs">{m.taskKey}</TableCell>
                    <TableCell className="text-xs">
                      {m.primaryProvider ?? '-'} / {m.primaryModel ?? '-'}
                    </TableCell>
                    <TableCell className="text-xs">
                      {m.fallbacks && m.fallbacks.length > 0
                        ? m.fallbacks.map((f, i) => (
                            <span key={i} className="mr-1 inline-block">
                              <Badge variant="secondary">
                                {f.provider}/{f.model}
                              </Badge>
                            </span>
                          ))
                        : '-'}
                    </TableCell>
                    <TableCell>
                      {m.isActive ? (
                        <Badge variant="success">Active</Badge>
                      ) : (
                        <Badge variant="secondary">Inactive</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {formatDate(m.updatedAt)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
